import { useNavigate } from "react-router-dom";
import { toast } from "components/ui/use-toast";
import { CAMPAIGN_MESSAGES, SLUG_STATUS_COPY } from "../../constants/messages";
import {
  CampaignSaveStatus,
  CampaignType,
  ICreateGamification,
  ISegmentationResult,
  IStore,
} from "@/@types/gamification";
import type { ICreateGamificationResponse } from "../../../../../services/gamification";
import {
  buildMechanicConfig,
  concurrencyPeriodParams,
} from "../../utils/mechanic-config";
import { buildCpfCsvFile } from "../../utils/segmentation";
import { bannerSourceKeyFor } from "../../utils/banner-source";
import { getApiErrorMessage } from "../../utils/api-error";
import { type CreateGamificationFormData } from "../../validations/gamification.validation";
import { LOCKED_MIN_PURCHASE } from "../../constants/mechanic-defaults";
import { LEAVE_WITHOUT_GUARD } from "../../wizard/useUnsavedChangesGuard";

interface UseFormSubmitProps {
  createCampaign: (data: ICreateGamification) => {
    unwrap: () => Promise<ICreateGamificationResponse>;
  };
  validatePeriod: (args: {
    startDate: string;
    endDate: string;
    campaignType?: CampaignType;
    competes?: boolean;
    group?: string;
  }) => {
    unwrap: () => Promise<{ valid: boolean; message: string }>;
  };
  uploadSegmentation: (args: { campaignId: number; file: File }) => {
    unwrap: () => Promise<ISegmentationResult>;
  };
  onSlugConflict?: (message: string) => void;
}

export interface SubmitOptions {
  draft?: boolean;
}

export const useFormSubmit = ({
  createCampaign,
  validatePeriod,
  uploadSegmentation,
  onSlugConflict,
}: UseFormSubmitProps) => {
  const navigate = useNavigate();

  const uploadSegmentationBase = async (
    created: ICreateGamificationResponse,
    file: File,
  ) => {
    const campaignId = created.data?.idCapa ?? created.data?.id;
    if (!campaignId) {
      toast({
        title: "Aviso",
        description:
          "Campanha criada, mas não foi possível identificar o ID para enviar a base de CPFs. Reenvie na edição.",
        variant: "destructive",
      });
      return;
    }

    try {
      const report = await uploadSegmentation({ campaignId, file }).unwrap();
      toast({
        title: "Base de CPFs processada",
        description: `${report.valid} válido(s), ${report.invalid} rejeitado(s), ${report.duplicates} duplicado(s) de ${report.total} recebido(s).`,
      });
    } catch {
      toast({
        title: "Aviso",
        description:
          "Campanha criada, mas o envio da base de CPFs falhou. Reenvie o arquivo pela edição da campanha.",
        variant: "destructive",
      });
    }
  };

  const onSubmit = async (
    data: CreateGamificationFormData,
    options: SubmitOptions = {},
  ): Promise<boolean> => {
    const isDraft = options.draft === true;
    try {
      const mechanic = data.campaignType ?? CampaignType.ROULETTE;

      if (!isDraft) {
        let periodValidation: { valid: boolean; message: string };
        try {
          periodValidation = await validatePeriod({
            startDate: data.startDate,
            endDate: data.endDate,
            campaignType: mechanic,
            ...concurrencyPeriodParams(data.mechanicConfig),
          }).unwrap();
        } catch (error) {
          toast({
            title: "Não foi possível validar o período",
            description: getApiErrorMessage(
              error,
              "Não foi possível validar o período da campanha. Tente novamente.",
            ),
            variant: "destructive",
          });
          return false;
        }

        if (!periodValidation.valid) {
          toast({
            title: "Período indisponível",
            description:
              periodValidation.message ||
              "O período informado conflita com outra campanha ativa do mesmo tipo.",
            variant: "destructive",
          });
          return false;
        }
      }

      const payload: ICreateGamification = {
        campaignName: data.campaignName.trim(),
        slug: data.slug?.trim() || undefined,
        startDate: data.startDate,
        endDate: data.endDate,
        campaignType: mechanic,
        stores: (data.stores ?? []) as IStore[],
        mechanicConfig: {
          ...buildMechanicConfig(data.mechanicConfig, mechanic),
          minPurchase: LOCKED_MIN_PURCHASE,
        },
        faqItems: data.faqItems ?? [],
        status: isDraft ? CampaignSaveStatus.DRAFT : CampaignSaveStatus.PUBLISHED,
        regulations: data.regulations,
        banner3x4: data.banner3x4,
        banner16x9: data.banner16x9,
        banner3x4SourceKey: bannerSourceKeyFor(data.banner3x4, data.banner3x4SourceKey),
        banner16x9SourceKey: bannerSourceKeyFor(data.banner16x9, data.banner16x9SourceKey),
        allStoresSelected: data.allStoresSelected,
      };

      const created = await createCampaign(payload).unwrap();

      const cpfs = data.segmentationCpfs ?? [];
      const shouldUploadSegmentation =
        data.mechanicConfig?.cpfSegmentation?.enabled && cpfs.length > 0;
      if (shouldUploadSegmentation) {
        await uploadSegmentationBase(created, buildCpfCsvFile(cpfs));
      }

      if (isDraft) {
        toast({
          variant: "default",
          description: "Rascunho salvo",
          duration: 5000,
        });
      }
      if (!isDraft) {
        toast(CAMPAIGN_MESSAGES.CREATE.SUCCESS);
      }
      navigate("/crm/gestao-gameficacao", { state: LEAVE_WITHOUT_GUARD });
      return true;
    } catch (error: unknown) {
      const description: string = getApiErrorMessage(
        error,
        CAMPAIGN_MESSAGES.CREATE.ERROR.description,
      );
      toast({ ...CAMPAIGN_MESSAGES.CREATE.ERROR, description });
      if (description === SLUG_STATUS_COPY.TAKEN) onSlugConflict?.(description);
      return false;
    }
  };

  return { onSubmit };
};
